if (!customElements.get("parts-collection-category-nav")) {
  class PartsCollectionCategoryNav extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.shell = this.querySelector("[data-parts-category-nav-shell]");
      this.track = this.querySelector("[data-parts-category-nav-track]");
      this.buttons = [
        ...this.querySelectorAll("[data-parts-category-nav-scroll]"),
      ];
      this.submenuEnabled = this.dataset.categorySubmenus === "true";
      this.submenuTriggers = [
        ...this.querySelectorAll("[data-parts-category-submenu-trigger]"),
      ];
      this.submenus = [
        ...this.querySelectorAll("[data-parts-category-submenu]"),
      ];
      this.submenuCaret = this.querySelector(
        "[data-parts-category-submenu-caret]",
      );
      this.activeSubmenuTrigger = null;
      this.categoryItems = [
        ...(this.track?.querySelectorAll(".fitment-category-nav__item") || []),
      ];
      this.mobileProgress = this.querySelector(
        "[data-parts-category-nav-progress]",
      );
      this.mobileProgressCurrent = this.querySelector(
        "[data-parts-category-progress-current]",
      );
      this.mobileProgressTotal = this.querySelector(
        "[data-parts-category-progress-total]",
      );
      this.mobileProgressDirection = this.querySelector(
        "[data-parts-category-progress-direction]",
      );
      this.stickyOffsetFrame = null;
      this.requestStickyOffsetUpdate = () => {};

      if (!this.shell || !this.track) return;

      this.stickyStack = this.mountStickyStack();
      this.bindThemeEditorPreview();
      this.bindScrollControls();
      this.bindProgressSwipe();
      this.bindStickyOffset();
      this.bindCategorySubmenus();
      this.bindFitmentReturnLinks();
      this.centerActiveItem();
    }

    bindThemeEditorPreview() {
      if (!this.hasAttribute("data-theme-editor-preview")) return;

      this.addEventListener("click", (event) => {
        if (event.target.closest("a")) event.preventDefault();
      });
    }

    bindCategorySubmenus() {
      if (!this.submenuEnabled || !this.submenuTriggers.length) return;

      this.submenuTriggers.forEach((trigger) => {
        trigger.addEventListener("click", (event) => {
          if (
            event.button !== 0 ||
            event.metaKey ||
            event.ctrlKey ||
            event.shiftKey ||
            event.altKey
          ) {
            return;
          }

          event.preventDefault();
          const submenuId = trigger.dataset.partsCategorySubmenuTrigger;
          const submenu = this.submenus.find(
            (item) => item.dataset.partsCategorySubmenu === submenuId,
          );
          if (!submenu) return;

          const shouldOpen = !submenu.classList.contains("is-open");
          this.closeCategorySubmenus();

          if (shouldOpen) {
            this.activeSubmenuTrigger = trigger;
            this.positionSubmenuCaret(trigger);
            this.submenuCaret?.classList.add("is-visible");
            submenu.classList.add("is-open");
            submenu.setAttribute("aria-hidden", "false");
            trigger.setAttribute("aria-expanded", "true");
            this.submenuTriggers.forEach((item) =>
              item.classList.toggle("is-active", item === trigger),
            );
            this.loadSubmenuCounts(submenu);
          }

          this.requestStickyOffsetUpdate();
        });
      });

      document.addEventListener("click", (event) => {
        if (!this.contains(event.target)) this.closeCategorySubmenus();
      });

      document.addEventListener("keydown", (event) => {
        if (event.key === "Escape") this.closeCategorySubmenus();
      });
    }

    closeCategorySubmenus() {
      const hadOpenSubmenu = this.submenus.some((submenu) =>
        submenu.classList.contains("is-open"),
      );
      this.submenus.forEach((submenu) => {
        submenu.classList.remove("is-open");
        submenu.setAttribute("aria-hidden", "true");
      });
      this.submenuTriggers.forEach((trigger) => {
        trigger.setAttribute("aria-expanded", "false");
        trigger.classList.toggle(
          "is-active",
          trigger.hasAttribute("aria-current"),
        );
      });
      this.activeSubmenuTrigger = null;
      this.submenuCaret?.classList.remove("is-visible");
      if (hadOpenSubmenu) this.requestStickyOffsetUpdate();
    }

    positionSubmenuCaret(trigger) {
      if (!trigger || !this.submenuCaret || !this.shell) return;

      const triggerRect = trigger.getBoundingClientRect();
      const shellRect = this.shell.getBoundingClientRect();
      this.submenuCaret.style.left = `${
        triggerRect.left - shellRect.left + triggerRect.width / 2
      }px`;
    }

    async loadSubmenuCounts(submenu) {
      if (submenu.dataset.countsLoaded === "true") return;

      const countElements = [
        ...submenu.querySelectorAll("[data-parts-submenu-count-query]"),
      ];
      if (!countElements.length) {
        submenu.dataset.countsLoaded = "true";
        return;
      }

      const requests = countElements.map((element) => {
        const collectionId = element.dataset.collectionId;
        const subtype = this.escapeAlgoliaFilterValue(element.dataset.subtype);
        const filters = [
          "status:ACTIVE",
          "NOT metafield_seo_hidden:1",
          `collection_ids:${collectionId}`,
          `metafield_product_subtype:\"${subtype}\"`,
        ];
        const params = new URLSearchParams({
          query: "",
          hitsPerPage: "0",
          filters: filters.join(" AND "),
        });

        return {
          indexName: "shopify_products_manual_index_prod",
          params: params.toString(),
        };
      });

      try {
        const response = await fetch(
          "https://RVBE1UCZU9-dsn.algolia.net/1/indexes/*/queries",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-Algolia-API-Key": "e37d703f9256b4f42b7dc520dca86cce",
              "X-Algolia-Application-Id": "RVBE1UCZU9",
            },
            body: JSON.stringify({ requests }),
          },
        );
        if (!response.ok)
          throw new Error(`Count request failed: ${response.status}`);

        const payload = await response.json();
        countElements.forEach((element, index) => {
          const count = Number(payload.results?.[index]?.nbHits || 0);
          if (count === 0) {
            element.closest(".parts-category-submenu__link")?.remove();
            return;
          }
          element.textContent = `${count} ${count === 1 ? "product" : "products"}`;
        });
        submenu.dataset.countsLoaded = "true";
        this.requestStickyOffsetUpdate();
      } catch (_error) {
        countElements.forEach((element) => {
          element.textContent = "View parts";
        });
        submenu.dataset.countsLoaded = "true";
      }
    }

    escapeAlgoliaFilterValue(value = "") {
      return value.replace(/\\/g, "\\\\").replace(/\"/g, '\\"');
    }

    mountStickyStack() {
      const existingStack = this.closest(".parts-collection-sticky-stack");
      if (existingStack) return existingStack;

      const controls = document.querySelector(
        ".shopify-section-collection-page main-collection .collection-controls",
      );
      const resultsColumn = controls?.parentElement;
      if (!controls || !resultsColumn) return null;

      const stack = document.createElement("div");
      stack.className = "parts-collection-sticky-stack";
      resultsColumn.insertBefore(stack, controls);
      stack.append(this);

      return stack;
    }

    bindFitmentReturnLinks() {
      const parentUrl = this.dataset.parentFitmentUrl;
      if (!parentUrl || !this.wasOpenedFromParentFitment(parentUrl)) return;

      this.prefetchParentFitment(parentUrl);

      const returnLinks = [
        ...document.querySelectorAll(
          "[data-fitment-parent-return][data-fitment-return-target]",
        ),
        ...this.querySelectorAll(
          "[data-fitment-return-target]:not([data-parts-category-submenu-trigger])",
        ),
      ];

      returnLinks.forEach((link) => {
        link.addEventListener("click", (event) => {
          if (
            event.defaultPrevented ||
            event.button !== 0 ||
            event.metaKey ||
            event.ctrlKey ||
            event.shiftKey ||
            event.altKey
          ) {
            return;
          }

          const targetId = link.dataset.fitmentReturnTarget;
          if (!targetId) return;

          this.storeReturnTarget(parentUrl, targetId);
          // Let the link perform a direct navigation. Using history.back() here
          // restored the child page's scroll position before the parent loaded,
          // which made the click look stalled or broken.
        });
      });
    }

    prefetchParentFitment(parentUrl) {
      const addPrefetch = () => {
        try {
          const parent = new URL(parentUrl, window.location.origin);
          if (parent.origin !== window.location.origin) return;

          parent.hash = "";
          const alreadyPrefetched = [
            ...document.querySelectorAll('link[rel="prefetch"]'),
          ].some((link) => link.href === parent.href);
          if (alreadyPrefetched) return;

          const prefetch = document.createElement("link");
          prefetch.rel = "prefetch";
          prefetch.href = parent.href;
          document.head.append(prefetch);
        } catch (_error) {
          // The normal return link remains fully functional if prefetching fails.
        }
      };

      if ("requestIdleCallback" in window) {
        window.requestIdleCallback(addPrefetch, { timeout: 1500 });
      } else {
        window.setTimeout(addPrefetch, 250);
      }
    }

    wasOpenedFromParentFitment(parentUrl) {
      if (!document.referrer || window.history.length < 2) return false;

      try {
        const referrer = new URL(document.referrer);
        const parent = new URL(parentUrl, window.location.origin);
        return (
          referrer.origin === parent.origin &&
          referrer.pathname === parent.pathname
        );
      } catch (_error) {
        return false;
      }
    }

    storeReturnTarget(parentUrl, targetId) {
      try {
        const parent = new URL(parentUrl, window.location.origin);
        window.sessionStorage.setItem(
          "dpp:fitment-return-target",
          JSON.stringify({
            pathname: parent.pathname,
            targetId,
            createdAt: Date.now(),
          }),
        );
        return true;
      } catch (_error) {
        return false;
      }
    }

    bindScrollControls() {
      const updateControls = () => this.updateScrollControls();

      this.track.addEventListener("scroll", updateControls, { passive: true });
      this.buttons.forEach((button) => {
        button.addEventListener("click", () => {
          const direction =
            button.dataset.partsCategoryNavScroll === "prev" ? -1 : 1;
          const distance = Math.max(this.track.clientWidth * 0.72, 180);

          this.track.scrollBy({
            left: direction * distance,
            behavior: "smooth",
          });
        });
      });

      window.addEventListener("resize", updateControls);
      window.requestAnimationFrame(updateControls);
    }

    bindProgressSwipe() {
      if (!this.mobileProgress) return;

      let activePointerId = null;
      let startX = 0;
      let startScrollLeft = 0;

      const stopDragging = (event) => {
        if (
          activePointerId === null ||
          (event && event.pointerId !== activePointerId)
        ) {
          return;
        }

        activePointerId = null;
        this.mobileProgress.classList.remove("is-dragging");
      };

      this.mobileProgress.addEventListener("pointerdown", (event) => {
        if (
          activePointerId !== null ||
          (event.pointerType === "mouse" && event.button !== 0)
        ) {
          return;
        }

        activePointerId = event.pointerId;
        startX = event.clientX;
        startScrollLeft = this.track.scrollLeft;
        this.mobileProgress.classList.add("is-dragging");

        try {
          this.mobileProgress.setPointerCapture(event.pointerId);
        } catch (_error) {
          // Synthetic pointer events may not support pointer capture.
        }
      });

      this.mobileProgress.addEventListener("pointermove", (event) => {
        if (event.pointerId !== activePointerId) return;

        const distance = event.clientX - startX;
        if (Math.abs(distance) < 2) return;

        this.track.scrollLeft = startScrollLeft - distance;
        if (event.cancelable) event.preventDefault();
      });

      this.mobileProgress.addEventListener("pointerup", stopDragging);
      this.mobileProgress.addEventListener("pointercancel", stopDragging);
      this.mobileProgress.addEventListener("lostpointercapture", stopDragging);
    }

    bindStickyOffset() {
      const header = document.getElementById("theme-header");
      const updateOffset = () => {
        const headerBottom = header
          ? Math.max(0, header.getBoundingClientRect().bottom)
          : 0;
        const gap = window.matchMedia("(max-width: 991px)").matches ? 0 : 12;
        const navHeight = this.getBoundingClientRect().height;

        this.style.setProperty(
          "--fitment-category-nav-top",
          `${Math.round(headerBottom + gap)}px`,
        );
        this.stickyStack?.style.setProperty(
          "--fitment-category-nav-top",
          `${Math.round(headerBottom + gap)}px`,
        );
        document.documentElement.style.setProperty(
          "--parts-collection-controls-top",
          `${Math.round(headerBottom + gap + navHeight)}px`,
        );
      };

      const scheduleUpdate = () => {
        if (this.stickyOffsetFrame) return;
        this.stickyOffsetFrame = window.requestAnimationFrame(() => {
          this.stickyOffsetFrame = null;
          updateOffset();
        });
      };

      const syncAfterLayout = () => {
        scheduleUpdate();
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(scheduleUpdate);
        });
      };
      this.requestStickyOffsetUpdate = syncAfterLayout;

      updateOffset();
      window.addEventListener("scroll", scheduleUpdate, { passive: true });
      window.addEventListener("resize", scheduleUpdate);
      window.addEventListener("load", syncAfterLayout, { once: true });
      window.addEventListener("pageshow", syncAfterLayout);

      if (header && "ResizeObserver" in window) {
        this.headerResizeObserver = new ResizeObserver(scheduleUpdate);
        this.headerResizeObserver.observe(header);
        this.headerResizeObserver.observe(this);
      }

      if (header && "MutationObserver" in window) {
        this.headerMutationObserver = new MutationObserver(syncAfterLayout);
        this.headerMutationObserver.observe(header, {
          attributes: true,
          attributeFilter: ["class", "style"],
        });
      }

      document.fonts?.ready.then(syncAfterLayout);
    }

    centerActiveItem() {
      const activeItem = this.track.querySelector(".is-active");
      if (!activeItem) return;

      window.requestAnimationFrame(() => {
        const targetLeft =
          activeItem.offsetLeft -
          (this.track.clientWidth - activeItem.offsetWidth) / 2;
        const maxScroll = Math.max(
          0,
          this.track.scrollWidth - this.track.clientWidth,
        );

        this.track.scrollLeft = Math.min(Math.max(0, targetLeft), maxScroll);
        this.updateScrollControls();
      });
    }

    updateScrollControls() {
      const trackHeight = this.track.offsetHeight;
      const maxScroll = Math.max(
        0,
        this.track.scrollWidth - this.track.clientWidth,
      );
      const currentScroll = this.track.scrollLeft;
      const hasOverflow = maxScroll > 2;

      if (trackHeight > 0) {
        this.shell.style.setProperty(
          "--parts-category-nav-track-height",
          `${trackHeight}px`,
        );
      }

      this.shell.classList.toggle("has-overflow", hasOverflow);
      this.shell.classList.toggle("can-scroll-prev", currentScroll > 2);
      this.shell.classList.toggle(
        "can-scroll-next",
        currentScroll < maxScroll - 2,
      );

      this.buttons.forEach((button) => {
        const isPrevious = button.dataset.partsCategoryNavScroll === "prev";
        button.disabled = isPrevious
          ? currentScroll <= 2
          : currentScroll >= maxScroll - 2;
      });

      this.updateMobileProgress(maxScroll, currentScroll);
      this.positionSubmenuCaret(this.activeSubmenuTrigger);
    }

    updateMobileProgress(maxScroll, currentScroll) {
      if (!this.mobileProgress || !this.categoryItems.length) return;

      const total = this.categoryItems.length;
      const atStart = currentScroll <= 2;
      const atEnd = maxScroll > 2 && currentScroll >= maxScroll - 2;
      const scrollRatio = maxScroll > 2 ? currentScroll / maxScroll : 0;
      const fillProgress = scrollRatio * 100;
      let currentCategory = 1;

      if (atEnd) {
        currentCategory = total;
      } else if (!atStart) {
        const trackRect = this.track.getBoundingClientRect();
        const trackCenter = trackRect.left + trackRect.width / 2;
        let nearestDistance = Number.POSITIVE_INFINITY;

        this.categoryItems.forEach((item, index) => {
          const itemRect = item.getBoundingClientRect();
          const itemCenter = itemRect.left + itemRect.width / 2;
          const distance = Math.abs(itemCenter - trackCenter);

          if (distance < nearestDistance) {
            nearestDistance = distance;
            currentCategory = index + 1;
          }
        });
      }

      const direction = atStart ? "start" : atEnd ? "end" : "middle";

      this.mobileProgress.style.setProperty(
        "--parts-category-scroll-progress",
        `${Math.min(100, Math.max(0, fillProgress)).toFixed(2)}%`,
      );
      this.mobileProgress.dataset.scrollDirection = direction;
      if (this.mobileProgressDirection) {
        this.mobileProgressDirection.dataset.partsCategoryProgressDirection =
          direction;
      }
      if (this.mobileProgressCurrent) {
        this.mobileProgressCurrent.textContent = String(currentCategory);
      }
      if (this.mobileProgressTotal) {
        this.mobileProgressTotal.textContent = String(total);
      }
    }
  }

  customElements.define(
    "parts-collection-category-nav",
    PartsCollectionCategoryNav,
  );
}
